import React, {useEffect, useState} from 'react';
import {
    Box,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Divider,
    TextField,
    Tooltip,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import instance from '../../../instance';
import CallNotification from '../../../context/NotificationContext';
import HelpPopover from '../../../ui/HelpPopover';
import {PaymentMethod} from '../../../Enums/PaymentMethod';
import InvoicePopup from '../../PaymentPage/Invoice/InvoicePopup';

interface IHistoryPage {
    userId: number | null;
}

interface IPayment {
    create_date: string;
    total_amount: number;
    payment_status?: number;
    payment_status_name: string;
    order_code: string;
    payment_method: PaymentMethod;
    invoice_number: string;
}

interface IPaymentDetail {
    create_date: string;
    total_amount: number;
    payment_status_name: string;
    order_code: string;
    payment_snapshot: string;
}

const HistoryPage = ({userId}: IHistoryPage) => {
    const [paymentList, setPaymentList] = useState<IPayment[]>([]);
    const [visibleList, setVisibleList] = useState<IPayment[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');

    const [selectedPayment, setSelectedPayment] = useState<IPaymentDetail | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const {handleErrorNotification} = CallNotification();

    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

    const openInvoice = (orderId: string) => {
        setSelectedOrderId(orderId);
        setInvoiceOpen(true);
    };

    useEffect(() => {
        if (!userId) return;

        const fetchPaymentList = async () => {
            try {
                const response = await instance.post('/LK', {
                    method: 'GetPaymentList',
                    params: {user_id: userId},
                });
                handleErrorNotification(response);

                const sortedList = (response.data?.payment_list || []).sort(
                    (a: IPayment, b: IPayment) => new Date(b.create_date).getTime() - new Date(a.create_date).getTime(),
                );

                setPaymentList(sortedList);
                setVisibleList(sortedList);
            } catch (error) {
                console.error('Ошибка при загрузке истории заказов', error);
            } finally {
                setLoading(false);
            }
        };

        void fetchPaymentList();
    }, [userId]);

    const handleRowClick = async (orderCode: string) => {
        try {
            const response = await instance.post('/LK', {
                method: 'GetPaymentInfo',
                params: {order: orderCode, user_id: userId},
            });
            handleErrorNotification(response);

            if (response.data?.payment_info) {
                setSelectedPayment(response.data.payment_info);
                setDetailOpen(true);
            }
        } catch (error) {
            console.error('Ошибка при получении информации о платеже', error);
        }
    };

    const handleClose = () => {
        setDetailOpen(false);
        setSelectedPayment(null);
    };

    const applyFilter = () => {
        if (!dateFrom && !dateTo) {
            setVisibleList(paymentList);
            return;
        }

        const start = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
        const end = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;

        let s = start,
            e = end;
        if (s && e && s.getTime() > e.getTime()) {
            const tmp = s;
            s = e;
            e = tmp;
        }

        const filtered = paymentList.filter(p => {
            const d = new Date(p.create_date).getTime();
            const okStart = s ? d >= s.getTime() : true;
            const okEnd = e ? d <= e.getTime() : true;
            return okStart && okEnd;
        });

        setVisibleList(filtered);
    };

    const paidOnly = visibleList.filter(p => p.payment_status === 1);
    const paidCount = paidOnly.length;
    const paidTotal = paidOnly.reduce((sum, p) => sum + (p.total_amount || 0), 0);

    const renderPaymentDetails = () => {
        if (!selectedPayment) return null;

        let snapshot;
        try {
            snapshot = JSON.parse(selectedPayment.payment_snapshot);
        } catch {
            snapshot = null;
        }

        if (!snapshot || !Array.isArray(snapshot.items))
            return (
                <>
                    <Typography variant="subtitle1" gutterBottom>
                        Заказ № {selectedPayment.order_code}
                    </Typography>
                    <Typography variant="body2">Дата: {new Date(selectedPayment.create_date).toLocaleString()}</Typography>
                    <Typography variant="body2">Сумма: {selectedPayment.total_amount} ₸</Typography>
                    <Typography variant="body2">Статус: {selectedPayment.payment_status_name}</Typography>
                </>
            );

        const grouped = snapshot.items.reduce((acc: Record<number, any>, item: any) => {
            if (!acc[item.facial_id]) {
                acc[item.facial_id] = {
                    facial_name: item.facial_name,
                    facial_inn: item.facial_inn,
                    items: [],
                };
            }
            acc[item.facial_id].items.push(item);
            return acc;
        }, {});

        const getPriceString = (item: any) => {
            if (item.quantity && item.quantity > 1) {
                return `${item.quantity} x ${item.unit_price_no_vat} = ${item.price_no_vat}`;
            }
            return item.price_no_vat;
        };

        return (
            <>
                <Typography variant="subtitle1" gutterBottom>
                    Заказ № {selectedPayment.order_code}
                </Typography>
                <Typography variant="body2">Дата: {new Date(selectedPayment.create_date).toLocaleString()}</Typography>
                <Typography variant="body2">Сумма: {selectedPayment.total_amount} ₸</Typography>
                <Typography variant="body2">Статус: {selectedPayment.payment_status_name}</Typography>

                <Divider sx={{my: 2}} />

                {Object.values(grouped).map((group: any, idx: number) => (
                    <Box
                        key={idx}
                        sx={{
                            mb: 3,
                            overflowX: 'auto',
                        }}
                    >
                        <Typography variant="subtitle2" sx={{mb: 1}}>
                            {group.facial_name} (ИНН: {group.facial_inn})
                        </Typography>
                        <Table
                            size="small"
                            sx={{
                                '& td, & th': {whiteSpace: 'nowrap'},
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Модель</TableCell>
                                    <TableCell>Серийный №</TableCell>
                                    <TableCell>Рег. №</TableCell>
                                    <TableCell>ОФД</TableCell>
                                    <TableCell>Без НДС</TableCell>
                                    <TableCell>С НДС</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {group.items.map((item: any, i: number) => (
                                    <TableRow key={i}>
                                        <TableCell>{item.model || '—'}</TableCell>
                                        <TableCell>{item.serial_number || '—'}</TableCell>
                                        <TableCell>{item.reg_number || '—'}</TableCell>
                                        <TableCell>{item.ofd?.name || '—'}</TableCell>
                                        <TableCell>{getPriceString(item)} ₸</TableCell>
                                        <TableCell>{item.price_with_vat} ₸</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                ))}

                <Divider sx={{my: 2}} />
                <Typography variant="body2">Итого без НДС: {snapshot.total_no_vat} ₸</Typography>
                <Typography variant="body2">
                    НДС{snapshot.vat_rate ? ` ${snapshot.vat_rate * 100}%` : ''}: {snapshot.vat} ₸
                </Typography>
                <Typography variant="body2">Итого с НДС: {snapshot.total_with_vat} ₸</Typography>
            </>
        );
    };

    return (
        <Container maxWidth="lg" sx={{pt: 6, pb: 6}}>
            <Typography variant="h4" gutterBottom>
                История заказов
            </Typography>

            <Paper sx={{mb: 2}}>
                <Box
                    component="form"
                    onSubmit={e => {
                        e.preventDefault();
                        applyFilter();
                    }}
                    p={2}
                    display="grid"
                    gridTemplateColumns={{xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)'}}
                    gap={2}
                    alignItems="end"
                >
                    <TextField
                        label="От"
                        type="date"
                        value={dateFrom}
                        onChange={e => setDateFrom(e.target.value)}
                        InputLabelProps={{shrink: true}}
                    />
                    <TextField
                        label="До"
                        type="date"
                        value={dateTo}
                        onChange={e => setDateTo(e.target.value)}
                        InputLabelProps={{shrink: true}}
                    />
                    <Box>
                        <Button variant="outlined" onClick={applyFilter} sx={{mr: 1, height: 40}}>
                            Найти
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => {
                                setDateFrom('');
                                setDateTo('');
                                setVisibleList(paymentList);
                            }}
                            sx={{height: 40}}
                        >
                            Сброс
                        </Button>
                    </Box>

                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Оплаченных заказов за период: {paidCount}
                            <br />
                            На сумму: {new Intl.NumberFormat('ru-RU').format(paidTotal)} ₸
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Paper>
                <Box p={3} sx={{overflowX: 'auto'}}>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <Table
                            size="small"
                            sx={{
                                '& td, & th': {whiteSpace: 'nowrap'},
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Дата</TableCell>
                                    <TableCell>Номер заказа</TableCell>
                                    <TableCell>
                                        Счёт
                                        <HelpPopover content={<>Номер счёта при оплате через банковские реквизиты.</>} />
                                    </TableCell>
                                    <TableCell>Сумма</TableCell>
                                    <TableCell>Статус</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            <Typography color="text.secondary">Нет записей по выбранному фильтру</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    visibleList.map((item, index) => (
                                        <TableRow
                                            key={index}
                                            hover
                                            sx={{
                                                cursor: 'pointer',
                                                '& td': {
                                                    borderBottom: index === visibleList.length - 1 ? 'none' : undefined,
                                                },
                                            }}
                                            onClick={() => void handleRowClick(item.order_code)}
                                        >
                                            <TableCell>{new Date(item.create_date).toLocaleString()}</TableCell>
                                            <TableCell>{item.order_code}</TableCell>
                                            <TableCell sx={{paddingTop: 0, paddingBottom: 0}}>
                                                {item?.payment_method === PaymentMethod.Invoice ? (
                                                    <>
                                                        <Tooltip title="Скачать или распечатать">
                                                            <Button
                                                                size="small"
                                                                color="primary"
                                                                variant="text"
                                                                startIcon={<DescriptionOutlinedIcon sx={{fontSize: 16}} />}
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    openInvoice(item.order_code);
                                                                }}
                                                                sx={{fontSize: 12, height: 20}}
                                                            >
                                                                {item.invoice_number}
                                                            </Button>
                                                        </Tooltip>
                                                    </>
                                                ) : (
                                                    <>&mdash;</>
                                                )}
                                            </TableCell>
                                            <TableCell>{item.total_amount} ₸</TableCell>
                                            <TableCell sx={{color: item.payment_status === 1 ? 'success.main' : 'inherit'}}>
                                                {item.payment_status_name}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </Paper>

            <Dialog open={detailOpen} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>Детали платежа</DialogTitle>
                <DialogContent dividers>{selectedPayment ? renderPaymentDetails() : <Typography>Загрузка...</Typography>}</DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="outlined">
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>

            <InvoicePopup
                open={invoiceOpen}
                setOpen={setInvoiceOpen}
                orderId={selectedOrderId}
                userId={userId}
                onClose={() => setInvoiceOpen(false)}
            />
        </Container>
    );
};

export default HistoryPage;
